import React, { useMemo } from "react";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";
import { getStatConfig, isWeaponStat, getStatsByCategory } from "@/lib/config/unit-comparison-config";
import { CheckCircle2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { WeaponImage } from "@/components/WeaponImage/WeaponImage";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";

interface ComparisonDisplayProps {
  units: Unit[];
  selectedStats: string[];
  weaponData: Map<string, WeaponData[]>;
  selectedWeapons: Map<string, number[]>;
  onWeaponSelect: (unitDescriptor: string, weaponIndexes: number[]) => void;
}

/**
 * Get stat value from unit
 */
function getStatValue(unit: Unit, statKey: string): number | null {
  switch (statKey) {
    case "speed":
      return unit.speed;
    case "roadSpeed":
      return unit.roadSpeed;
    case "rotationTime":
      return unit.rotationTime;
    case "fuel":
      return unit.fuel;
    case "fuelMove":
      return unit.fuelMove;
    case "supply":
      return unit.supply;
    case "advancedDeployment":
      return unit.advancedDeployment;
    case "travelTime":
      return unit.travelTime ?? null;
    case "frontArmor":
      return unit.frontArmor;
    case "sideArmor":
      return unit.sideArmor;
    case "rearArmor":
      return unit.rearArmor;
    case "topArmor":
      return unit.topArmor;
    case "maxDamage":
      return unit.maxDamage;
    case "optics":
      return unit.optics;
    case "airOptics":
      return unit.airOptics;
    case "lowAltOptics":
      return unit.lowAltOptics ?? null;
    case "opticsRange":
      return unit.opticsRange ?? null;
    case "airOpticsRange":
      return unit.airOpticsRange ?? null;
    case "lowAltOpticsRange":
      return unit.lowAltOpticsRange ?? null;
    case "ecm":
      return unit.ecm;
    case "agility":
      return unit.agility ?? null;
    case "flyingAltitude":
      return unit.flyingAltitude ?? null;
    case "dangerousness":
      return unit.dangerousness ?? null;
    case "stealth":
      return unit.stealth;
    case "commandPoints":
      return unit.commandPoints;
    default:
      return null;
  }
}

/**
 * Get weapon stat value from weapon data
 */
function getWeaponStatValue(weapon: WeaponData | undefined, statKey: string): number | null {
  if (!weapon) return null;

  switch (statKey) {
    case "weapon_penetration":
      return weapon.ammo.penetration;
    case "weapon_heDamage":
      return weapon.ammo.heDamage;
    case "weapon_suppress":
      return weapon.ammo.suppress;
    case "weapon_groundMaxRange":
      return weapon.ammo.groundMaxRange;
    case "weapon_groundMinRange":
      return weapon.ammo.groundMinRange;
    case "weapon_heliMaxRange":
      return weapon.ammo.heliMaxRange;
    case "weapon_heliMinRange":
      return weapon.ammo.heliMinRange;
    case "weapon_planeMaxRange":
      return weapon.ammo.planeMaxRange;
    case "weapon_planeMinRange":
      return weapon.ammo.planeMinRange;
    case "weapon_staticAccuracy":
      return weapon.ammo.staticAccuracy;
    case "weapon_movingAccuracy":
      return weapon.ammo.movingAccuracy ?? null;
    case "weapon_rateOfFire":
      return weapon.ammo.rateOfFire;
    case "weapon_aimingTime":
      return weapon.ammo.aimingTime;
    case "weapon_reloadTime":
      return weapon.ammo.reloadTime;
    case "weapon_supplyCost":
      return weapon.supplyCost;
    default:
      return null;
  }
}

/**
 * Format stat value for display
 */
function formatStatValue(value: number | null, statKey: string): string {
  if (value === null || value === undefined) return "—";

  if (typeof value === "number") {
    switch (statKey) {
      case "speed":
      case "roadSpeed":
        return `${value} km/h`;
      case "rotationTime":
      case "travelTime":
      case "weapon_aimingTime":
      case "weapon_reloadTime":
        return `${value}s`;
      case "opticsRange":
      case "airOpticsRange":
      case "lowAltOpticsRange":
      case "advancedDeployment":
      case "flyingAltitude":
      case "weapon_groundMaxRange":
      case "weapon_groundMinRange":
      case "weapon_heliMaxRange":
      case "weapon_heliMinRange":
      case "weapon_planeMaxRange":
      case "weapon_planeMinRange":
        return `${value}m`;
      case "weapon_staticAccuracy":
      case "weapon_movingAccuracy":
        return `${value}%`;
      case "weapon_heDamage":
        return value.toFixed(3);
      default:
        return value.toString();
    }
  }

  return String(value);
}

/**
 * Represents a column in the comparison table
 */
interface TableColumn {
  unit: Unit;
  weapon?: WeaponData;
  weaponIndex?: number;
}

/**
 * Calculate comparison results for all stats across all unit-weapon combinations
 */
function calculateComparisonResults(
  units: Unit[],
  selectedStats: string[],
  weaponData: Map<string, WeaponData[]>,
  selectedWeapons: Map<string, number[]>,
  columns: TableColumn[]
): Map<string, Map<string, { best: boolean; worst: boolean }>> {
  const results = new Map<string, Map<string, { best: boolean; worst: boolean }>>();

  selectedStats.forEach((statKey) => {
    const config = getStatConfig(statKey);
    if (!config) return;

    const isWeaponStatKey = isWeaponStat(statKey);
    let values: Array<{ columnKey: string; value: number | null }>;

    if (isWeaponStatKey) {
      // For weapon stats: compare across all unit-weapon combinations
      values = columns.map((col) => {
        let value: number | null = null;
        if (col.weapon) {
          value = getWeaponStatValue(col.weapon, statKey);
        }
        return {
          columnKey: `${col.unit.descriptorName}-${col.weaponIndex ?? 'unit'}`,
          value,
        };
      });
    } else {
      // For unit stats: compare across units (one value per unit)
      const unitValues = new Map<string, number | null>();
      for (const unit of units) {
        const value = getStatValue(unit, statKey);
        unitValues.set(unit.descriptorName, value);
      }
      values = Array.from(unitValues.entries()).map(([unitDescriptor, value]) => ({
        columnKey: `${unitDescriptor}-unit`,
        value,
      }));
    }

    // Filter out null/undefined values
    const validValues = values.filter((v) => v.value !== null && v.value !== undefined);

    if (validValues.length === 0) return;

    // Find best and worst
    let bestValue: number;
    let worstValue: number;

    if (config.higherIsBetter) {
      bestValue = Math.max(...validValues.map((v) => v.value as number));
      worstValue = Math.min(...validValues.map((v) => v.value as number));
    } else {
      bestValue = Math.min(...validValues.map((v) => v.value as number));
      worstValue = Math.max(...validValues.map((v) => v.value as number));
    }

    // Only mark as best/worst if there's a clear difference
    if (bestValue !== worstValue) {
      const columnResults = new Map<string, { best: boolean; worst: boolean }>();
      
      validValues.forEach((v) => {
        const isBest = v.value === bestValue;
        const isWorst = v.value === worstValue;
        columnResults.set(v.columnKey, { best: isBest, worst: isWorst });
      });

      results.set(statKey, columnResults);
    }
  });

  return results;
}

export function ComparisonDisplay({
  units,
  selectedStats,
  weaponData,
  selectedWeapons,
  onWeaponSelect,
}: ComparisonDisplayProps) {
  // Build table columns: each unit gets columns for each selected weapon
  const columns = useMemo(() => {
    const cols: TableColumn[] = [];
    
    for (const unit of units) {
      const selectedWeaponIndexes = selectedWeapons.get(unit.descriptorName) || [];
      const weapons = weaponData.get(unit.weaponDescriptorName || "");
      
      if (weapons && selectedWeaponIndexes.length > 0) {
        // Add a column for each selected weapon
        for (const weaponIndex of selectedWeaponIndexes) {
          if (weapons[weaponIndex]) {
            cols.push({
              unit,
              weapon: weapons[weaponIndex],
              weaponIndex,
            });
          }
        }
      } else {
        // Unit with no weapons or no weapons selected - add unit-only column
        cols.push({ unit });
      }
    }
    
    return cols;
  }, [units, selectedWeapons, weaponData]);

  const comparisonResults = useMemo(
    () => calculateComparisonResults(units, selectedStats, weaponData, selectedWeapons, columns),
    [units, selectedStats, weaponData, selectedWeapons, columns]
  );

  if (units.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Select units to compare
      </div>
    );
  }

  // Group stats by category - Weapon first, then Misc, then others
  const categories = ["Weapon", "Misc", "Movement", "Armor", "Optics", "Flying"];
  const statsByCategory = categories.map((category) => {
    const categoryStats = getStatsByCategory(category);
    return {
      category,
      stats: categoryStats.filter((stat) => selectedStats.includes(stat.statKey)),
    };
  }).filter((cat) => cat.stats.length > 0);

  // Get column key for comparison results
  const getColumnKey = (col: TableColumn): string => {
    return `${col.unit.descriptorName}-${col.weaponIndex ?? 'unit'}`;
  };

  // Get stat value for a column
  const getValueForColumn = (col: TableColumn, statKey: string): number | null => {
    if (isWeaponStat(statKey)) {
      if (col.weapon) {
        return getWeaponStatValue(col.weapon, statKey);
      }
      return null;
    } else {
      return getStatValue(col.unit, statKey);
    }
  };

  // Group columns by unit for header display
  const columnsByUnit = useMemo(() => {
    const grouped = new Map<string, TableColumn[]>();
    for (const col of columns) {
      const unitKey = col.unit.descriptorName;
      if (!grouped.has(unitKey)) {
        grouped.set(unitKey, []);
      }
      grouped.get(unitKey)!.push(col);
    }
    return grouped;
  }, [columns]);

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="border-collapse mx-auto">
          <thead className="bg-background">
            {/* Unit header row */}
            <tr>
              <th className="sticky left-0 z-20 bg-background border-r border-b px-3 py-2 text-left w-[160px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Field
                </span>
              </th>
              {Array.from(columnsByUnit.entries()).map(([unitKey, unitColumns]) => {
                const firstColumn = unitColumns[0];
                if (!firstColumn) return null;
                return (
                  <th
                    key={unitKey}
                    colSpan={unitColumns.length}
                    className="border-b px-2 py-2 text-center bg-muted/50 w-[120px]"
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      <img
                        src={getUnitImagePath(firstColumn.unit.descriptorName)}
                        alt={firstColumn.unit.name}
                        className="h-10 w-auto object-contain max-w-[70px]"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <div className="font-semibold text-sm leading-tight">{firstColumn.unit.name}</div>
                      {firstColumn.unit.specialities && firstColumn.unit.specialities.length > 0 && (
                        <TraitBadgeList specialities={firstColumn.unit.specialities} size="sm" />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
            {/* Weapon header row */}
            {columns.some((col) => col.weapon) && (
              <tr>
                <th className="sticky left-0 z-20 bg-background border-r border-b px-3 py-2 shadow-[2px_0_4px_rgba(0,0,0,0.1)]"></th>
                {Array.from(columnsByUnit.entries()).map(([unitKey, unitColumns]) =>
                  unitColumns.map((col, idx) => (
                    <th
                      key={`${unitKey}-${idx}`}
                      className="border-b px-2 py-2 text-center bg-muted/30 text-xs font-medium w-[110px]"
                    >
                      {col.weapon ? (
                        <div className="flex flex-col items-center gap-1">
                          {col.weapon.ammo.textureId && (
                            <WeaponImage
                              textureId={col.weapon.ammo.textureId}
                              alt={col.weapon.ammo.name}
                              size="small"
                              showPlaceholder={false}
                              className="h-5 w-auto"
                            />
                          )}
                          <div className="font-medium leading-tight">
                            {col.weapon.ammo.name}
                            {col.weapon.numberOfWeapons > 1 && ` x${col.weapon.numberOfWeapons}`}
                          </div>
                        </div>
                      ) : (
                        "—"
                      )}
                    </th>
                  ))
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {statsByCategory.map(({ category, stats }) => (
              <React.Fragment key={category}>
                {/* Category header row */}
                <tr>
                  <td className="sticky left-0 z-10 bg-muted/30 border-r border-b px-3 py-1.5 font-semibold text-xs text-muted-foreground uppercase tracking-wide shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                    {category}
                  </td>
                  <td
                    colSpan={columns.length}
                    className="bg-muted/30 border-b px-3 py-1.5 font-semibold text-xs text-muted-foreground uppercase tracking-wide"
                  ></td>
                </tr>
                {/* Stat rows */}
                {stats.map((statConfig) => {
                  const statKey = statConfig.statKey;
                  const columnResults = comparisonResults.get(statKey);
                  const isWeaponStatKey = isWeaponStat(statKey);
                  
                  return (
                    <tr key={statKey} className="hover:bg-muted/30">
                      <td className="sticky left-0 z-10 bg-background border-r border-b px-3 py-1.5 text-sm font-medium w-[160px] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                        {statConfig.label}
                      </td>
                      {isWeaponStatKey ? (
                        // Weapon stats: show in each weapon column
                        columns.map((col) => {
                          const value = getValueForColumn(col, statKey);
                          const formattedValue = formatStatValue(value, statKey);
                          const colKey = getColumnKey(col);
                          const comparison = columnResults?.get(colKey);
                          const isBest = comparison?.best ?? false;
                          const isWorst = comparison?.worst ?? false;

                          return (
                            <td
                              key={colKey}
                              className={cn(
                                "border-b px-2 py-1.5 text-center text-sm",
                                isBest && "bg-green-500/10",
                                isWorst && !isBest && "bg-red-500/10"
                              )}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <span>{formattedValue}</span>
                                {isBest && (
                                  <CheckCircle2 className="h-3 w-3 text-green-600 dark:text-green-400 flex-shrink-0" />
                                )}
                                {isWorst && !isBest && (
                                  <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                                )}
                              </div>
                            </td>
                          );
                        })
                      ) : (
                        // Unit stats: merge across all weapon columns for each unit
                        Array.from(columnsByUnit.entries()).map(([unitKey, unitColumns]) => {
                          const firstColumn = unitColumns[0];
                          if (!firstColumn) return null;
                          
                          const value = getStatValue(firstColumn.unit, statKey);
                          const formattedValue = formatStatValue(value, statKey);
                          // For unit stats, use unit descriptor name as key
                          const unitKeyForComparison = `${firstColumn.unit.descriptorName}-unit`;
                          const comparison = columnResults?.get(unitKeyForComparison);
                          const isBest = comparison?.best ?? false;
                          const isWorst = comparison?.worst ?? false;

                          return (
                            <td
                              key={unitKey}
                              colSpan={unitColumns.length}
                              className={cn(
                                "border-b px-2 py-1.5 text-center text-sm",
                                isBest && "bg-green-500/10",
                                isWorst && !isBest && "bg-red-500/10"
                              )}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <span>{formattedValue}</span>
                                {isBest && (
                                  <CheckCircle2 className="h-3 w-3 text-green-600 dark:text-green-400 flex-shrink-0" />
                                )}
                                {isWorst && !isBest && (
                                  <XCircle className="h-3 w-3 text-red-600 dark:text-red-400 flex-shrink-0" />
                                )}
                              </div>
                            </td>
                          );
                        })
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
